/**
 * Mapbox styles for the Incidents layer. One GeoJSON source holds hulls,
 * links and markers (utils/incidentFeatures.ts); each style filters on
 * `feature_kind` and reads its colour from the feature, so status → colour
 * lives in exactly one place (utils/incidentRekindle.ts).
 */

const isKind = (kind: string) => ['==', ['get', 'feature_kind'], kind];

// Closed history is drawn faintly and dashed; open incidents solid. The
// selected incident (and its lineage) is emphasised rather than recoloured
// so the status colour still reads.
export const incidentHullFillStyle = (id: string) => ({
    id: `${id}-hull-fill`,
    type: 'fill' as const,
    filter: ['all', isKind('hull'), ['==', ['geometry-type'], 'Polygon']],
    paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': [
            'case',
            ['get', 'selected'],
            0.28,
            ['get', 'closed'],
            0.06,
            0.16,
        ],
    },
});

export const incidentHullLineStyle = (id: string) => ({
    id: `${id}-hull-line`,
    type: 'line' as const,
    filter: ['all', isKind('hull'), ['!=', ['geometry-type'], 'Point']],
    paint: {
        'line-color': ['get', 'color'],
        'line-width': ['case', ['get', 'selected'], 3, 1.5],
        'line-opacity': ['case', ['get', 'closed'], 0.6, 0.95],
        'line-dasharray': [
            'case',
            ['get', 'closed'],
            ['literal', [2, 2]],
            ['==', ['get', 'status'], 'stand_down'],
            ['literal', [1, 1.5]],
            ['literal', [1, 0]],
        ],
    },
});

export const incidentLinkStyle = (id: string) => ({
    id: `${id}-links`,
    type: 'line' as const,
    filter: isKind('link'),
    paint: {
        'line-color': ['get', 'color'],
        'line-width': ['case', ['get', 'selected'], 2.5, 1.5],
        'line-opacity': 0.9,
        'line-dasharray': ['literal', [1, 2]],
    },
});

export const incidentMarkerStyle = (id: string) => ({
    id: `${id}-markers`,
    type: 'circle' as const,
    filter: isKind('marker'),
    paint: {
        'circle-color': ['get', 'color'],
        'circle-radius': ['case', ['get', 'selected'], 15, 12],
        'circle-opacity': ['case', ['get', 'closed'], 0.75, 1],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': ['case', ['get', 'selected'], 2.5, 1.5],
    },
});

export const incidentMarkerLabelStyle = (id: string) => ({
    id: `${id}-marker-labels`,
    type: 'symbol' as const,
    filter: isKind('marker'),
    layout: {
        'text-field': ['get', 'label'],
        'text-size': 11,
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        'text-anchor': 'center',
        // The number is the point: never let a road name displace it.
        'text-allow-overlap': true,
        'text-ignore-placement': true,
    },
    paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(15, 23, 42, 0.6)',
        'text-halo-width': 0.8,
    },
});
